// The one place the client talks to the Go server. Everything goes through
// same-origin fetch with the session cookie, so there is no token handling here.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

type Init = RequestInit & { json?: unknown };

async function request(path: string, init: Init = {}): Promise<Response> {
  const { json, headers, ...rest } = init;
  const res = await fetch(path, {
    ...rest,
    headers: json
      ? { "Content-Type": "application/json", ...headers }
      : headers,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  if (!res.ok) {
    // The server sends validation problems as plain text, one per line — that
    // is what the forms display, so keep it verbatim.
    const message = (await res.text()) || res.statusText;
    throw new ApiError(res.status, message);
  }
  return res;
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return (await apiGetMeta<T>(path, signal)).data;
}

// Same request, plus where the bytes came from. The service worker stamps
// responses it served out of its cache with the date of the original reply;
// a live response has no such header, so cachedAt is null.
export async function apiGetMeta<T>(
  path: string,
  signal?: AbortSignal,
): Promise<{ data: T; cachedAt: string | null }> {
  const res = await request(path, { signal });
  return {
    data: (await res.json()) as T,
    cachedAt: res.headers.get("x-sw-cached-at"),
  };
}

export async function apiSend<T>(
  method: "POST" | "PATCH" | "DELETE",
  path: string,
  json?: unknown,
): Promise<T> {
  const res = await request(path, { method, json });
  return res.json() as Promise<T>;
}

// Multipart uploads (receipt capture, CSV import) — no JSON body.
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await request(path, { method: "POST", body: form });
  return res.json() as Promise<T>;
}

// Build a query string, dropping empty values so the URL stays clean.
export function query(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      sp.set(key, String(value));
    }
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}
